/**
 * Screen registry — the single source of truth describing every console screen
 * to the AI assistant. Each entry grounds the model in (a) what the screen is
 * for, (b) what the user can do there, (c) which MCP tools back those actions,
 * and (d) good starter questions.
 *
 * The registry is client-owned: the chat composable resolves the entry for the
 * current route and sends only that compact doc (plus live page context) to the
 * orchestrator, so the server never has to embed the whole site map in every
 * prompt. This keeps prompts small and the knowledge co-located with the UI.
 */
import { GENERATED_SCREENS } from './screens.generated'

export interface ScreenDoc {
  /** Stable id, also used as the page-context screenId. */
  id: string
  /** Route matcher: exact path or a prefix ending in `*`. */
  match: string
  title: string
  /** One or two sentences: what this screen is and what it shows. */
  summary: string
  /** Concrete things the user can do here (plain language). */
  capabilities: string[]
  /** MCP tool names relevant to this screen (helps the model pick tools). */
  relatedTools: string[]
  /** Click-to-ask starter questions shown when the widget opens on this screen. */
  suggestedPrompts: string[]
  /**
   * How to open this screen when it needs more than its path carries — shown
   * in the assistant's site map INSTEAD of the bare route, `:name` placeholders
   * included, so the link the model builds carries what the page needs. The
   * resize wizard opened as a bare `/resize-wizard` showed "Invalid instance".
   */
  link?: string
}

export const SCREENS: ScreenDoc[] = [
  {
    id: 'dashboard',
    match: '/',
    title: 'Dashboard',
    summary:
      'Account overview: what needs attention, the fleet of instances, money (balance, next charge) and protection (backups, firewalls).',
    capabilities: [
      'See an overview of your cloud resources and anything that needs attention',
      'Jump to instances, networking and billing',
    ],
    relatedTools: ['account_summary', 'billing_subscriptions', 'billing_invoices'],
    suggestedPrompts: [
      'Give me a summary of my account',
      'How many instances do I have running?',
      'What am I currently being billed for?',
    ],
  },
  {
    id: 'instances',
    match: '/instances',
    title: 'Instances',
    summary:
      'List of all virtual machines (cloud instances) with their status, size and addresses.',
    capabilities: [
      'View every instance and its status',
      'Open an instance to manage it, or order a new one',
    ],
    relatedTools: ['account_summary', 'vm_status', 'vm_operations'],
    suggestedPrompts: [
      'List my instances and their status',
      'Which of my instances are stopped?',
      'How do I create a new instance?',
    ],
  },
  {
    id: 'instance-detail',
    match: '/instances/*',
    title: 'Instance details',
    summary:
      'One instance (its id is the `id` route parameter): power controls, specs and IPs, monitoring, networking, firewall, snapshots, backups, browser console, hardware settings, reinstall and password reset.' +
      ' An instance that will not boot can be put in rescue mode (More menu → "Rescue mode…",' +
      ' or Settings → Rescue mode): it restarts into a temporary rescue system with its disks' +
      ' attached and untouched, reachable by SSH/SFTP and the browser console, while its own' +
      ' system and services stay offline until "Exit rescue mode".',
    capabilities: [
      'Inspect specs, IPs and live status; start, stop (a hard power-off) or reboot',
      'Review CPU, memory, disk and network metrics and data transfer',
      'Take or restore snapshots and backups, open the browser console, reset the password, reinstall the OS',
      'Boot into rescue mode to copy files off or repair a system that will not boot, then exit rescue mode',
    ],
    relatedTools: [
      'vm_get',
      'vm_status',
      'vm_metrics',
      'vm_bandwidth',
      'vm_snapshots',
      'vm_backups',
      'vm_operations',
      'firewall_get_for_vm',
      'vm_power',
      'vm_rescue_status',
      'vm_suspension_reason',
      'platform_help',
    ],
    suggestedPrompts: [
      'Why is this instance using so much CPU?',
      'What are the specs of this instance?',
      'Reboot this instance',
    ],
  },
  {
    id: 'billing',
    match: '/billing',
    title: 'Billing',
    summary:
      'Subscriptions, balances, the next charge and invoices for your cloud account.',
    capabilities: [
      'Review subscriptions and balances',
      'See open invoices and pay them',
      'Open a subscription for its usage, ledger and invoices',
    ],
    relatedTools: [
      'account_summary',
      'billing_subscriptions',
      'billing_invoices',
      'billing_subscription_get',
      'billing_ledger',
      'platform_help',
    ],
    suggestedPrompts: [
      'Explain my current bill',
      'What is my available balance?',
      'Show my recent ledger entries',
    ],
  },
  {
    id: 'billing-detail',
    match: '/billing/*',
    title: 'Subscription details',
    summary:
      'One subscription (its id is the `id` route parameter): term and renewal, balance by bucket, the resources it pays for, hourly usage, the ledger and its invoices; prepay, export the statement or request cancellation here.',
    capabilities: [
      'Understand the balance buckets and the next renewal',
      'See what each resource costs and how usage was charged',
      'Export the ledger, pay or prepay, request cancellation',
    ],
    relatedTools: ['billing_subscription_get', 'billing_ledger', 'billing_invoices', 'platform_help'],
    suggestedPrompts: [
      "Explain this subscription's balance",
      'What am I paying for here?',
      'When does this subscription renew?',
    ],
  },
  /*
   * Checkout is the single most valuable page in the app, and until now it
   * matched nothing: `/billing` is an exact pattern, so `/billing/checkout`
   * fell through to the generic fallback and the assistant greeted a customer
   * mid-purchase with "What can you help me with?".
   */
  {
    id: 'checkout',
    match: '/billing/checkout',
    title: 'Checkout',
    summary:
      'Ordering a new cloud instance: plan and term, datacenter, operating system, storage, add-ons, coupon and payment method.',
    capabilities: [
      'Explain what a plan includes and how the term affects the price',
      'Explain storage, traffic allowance and add-ons',
      'Price a configuration before ordering',
    ],
    relatedTools: ['catalog_plans', 'catalog_quote', 'catalog_locations', 'vm_list_templates', 'platform_help'],
    suggestedPrompts: [
      'Which plan fits my website?',
      'What does this term change on the price?',
      'Which payment methods do you accept?',
    ],
  },
  {
    id: 'vpn-gateway',
    match: '/vpn-gateway',
    title: 'VPN Gateway',
    summary:
      'Your managed VPN gateways (OpenVPN remote access into a private network): status, users, connection profile and settings. Edits are saved as pending until "Apply changes".',
    capabilities: [
      'See each gateway and its users',
      'Download the connection profile, change settings and apply pending changes',
    ],
    relatedTools: ['vpc_list', 'platform_help'],
    suggestedPrompts: [
      'How do I connect to my VPN?',
      'How do I add a VPN user?',
      "Why can't my VPN users reach my servers?",
    ],
  },
  {
    id: 'vpn-users',
    match: '/vpn-gateway/users',
    title: 'VPN Users',
    summary:
      'The people who can connect to your VPN gateways: add, disable or remove users and send them their credentials.',
    capabilities: ['Add, disable or remove VPN users', 'Send a user their credentials and profile link'],
    relatedTools: ['platform_help'],
    suggestedPrompts: [
      'How do I add a user?',
      'How do I send credentials to a user?',
      'What happens when I disable a user?',
    ],
  },
  {
    id: 'vpn-site-to-site',
    match: '/vpn-gateway/site-to-site',
    title: 'Site-to-site VPN',
    summary:
      'Permanent tunnels (IPsec IKEv2 or WireGuard) between a VPN gateway and another network, such as an office.',
    capabilities: ['Create, edit and remove site-to-site connections'],
    relatedTools: ['catalog_plans', 'platform_help'],
    suggestedPrompts: [
      'How do I connect my office network?',
      'IPsec or WireGuard — which should I use?',
      'How many site connections does my plan include?',
    ],
  },
  {
    id: 'vpn-site-to-site-detail',
    match: '/vpn-gateway/site-to-site/*',
    title: 'Site-to-site connection',
    summary: 'One site-to-site tunnel: its settings, the remote network and its status.',
    capabilities: ['Review or change one tunnel'],
    relatedTools: ['platform_help'],
    suggestedPrompts: [
      'Why is my tunnel not connecting?',
      'What do I configure on my side?',
      'How do I change the remote network?',
    ],
  },
  {
    id: 'vpn-checkout',
    match: '/vpn-gateway/checkout',
    title: 'VPN Gateway checkout',
    summary: 'Ordering a VPN gateway: location, private network, plan and billing term.',
    capabilities: [
      'Explain what a VPN gateway does and which location to pick',
      'Compare the plans and their prices',
    ],
    relatedTools: ['catalog_plans', 'catalog_locations', 'vpc_list', 'platform_help'],
    suggestedPrompts: [
      'What is a VPN gateway for?',
      'Which location should I choose?',
      'How many users does this support?',
    ],
  },
  {
    id: 'network-dns',
    match: '/network/dns',
    title: 'DNS',
    summary: 'Managed DNS zones (root domains hosted on this platform’s nameservers).',
    capabilities: ['View DNS zones and add a domain', 'Open a zone to manage its records'],
    relatedTools: ['dns_zone_list', 'dns_record_list', 'dns_zone_create', 'platform_help'],
    suggestedPrompts: [
      'List my DNS zones',
      'How do I add an A record?',
      'Which nameservers should my domain use?',
    ],
  },
  {
    id: 'network-dns-zone',
    match: '/network/dns/*',
    title: 'DNS zone',
    summary:
      'The records of one hosted domain (its id is the `zoneId` route parameter): add, edit or delete records, or point the domain to an instance.',
    capabilities: ['Add, edit or delete A, AAAA, CNAME, MX, TXT, CAA and SRV records', 'Point the domain to an instance'],
    relatedTools: ['dns_record_list', 'dns_record_add', 'dns_zone_list', 'platform_help'],
    suggestedPrompts: [
      'Where does this domain point?',
      'Add an A record for www',
      'How do I set up email records (MX and SPF)?',
    ],
  },
  {
    id: 'network-rdns',
    match: '/network/rdns',
    title: 'Reverse DNS',
    summary:
      'Set the reverse DNS (PTR) name of your IPv4 and IPv6 addresses. The name must already resolve to the address (an A/AAAA record) before it is accepted.',
    capabilities: ['Set or reset the PTR name of each of your IP addresses'],
    relatedTools: ['dns_record_list', 'platform_help'],
    suggestedPrompts: [
      'How do I set reverse DNS for my mail server?',
      'Why was my reverse DNS name refused?',
      'What is reverse DNS used for?',
    ],
  },
  {
    id: 'network-firewall',
    match: '/network/firewall',
    title: 'Firewall',
    summary: 'Cloud firewalls: rule sets you apply to one or more instances.',
    capabilities: ['View firewalls and the instances they protect', 'Create a firewall'],
    relatedTools: ['firewall_list', 'firewall_get_for_vm', 'firewall_macros', 'platform_help'],
    suggestedPrompts: [
      'List my firewalls',
      'How do I allow inbound HTTPS?',
      'Is port 22 open on my server?',
    ],
  },
  {
    id: 'network-firewall-detail',
    match: '/network/firewall/*',
    title: 'Firewall rules',
    summary:
      'One firewall (its id is the `id` route parameter): default inbound/outbound policies, ordered rules, and the instances it applies to. Edits are saved as pending and only reach the instances after "Apply".',
    capabilities: ['Add, edit, reorder and delete rules', 'Choose the instances it applies to', 'Apply pending changes'],
    relatedTools: ['firewall_list', 'firewall_macros', 'firewall_get_for_vm', 'platform_help'],
    suggestedPrompts: [
      'What do these rules allow?',
      'How do I open port 443?',
      'Why are my changes not applied yet?',
    ],
  },
  {
    id: 'network-vpc',
    match: '/network/vpc',
    title: 'Private networks (VPC)',
    summary: 'Private networks that connect instances in the same location over private IP addresses.',
    capabilities: ['Create a private network', 'Open one to add or remove instances'],
    relatedTools: ['vpc_list', 'platform_help'],
    suggestedPrompts: [
      'What is a VPC for?',
      'Which instances are on my private network?',
      'Can my servers in different locations share a VPC?',
    ],
  },
  {
    id: 'network-vpc-detail',
    match: '/network/vpc/*',
    title: 'Private network',
    summary:
      'One private network (its id is the `id` route parameter): its subnet and member instances with their private IPs. Adding or removing members can restart instances.',
    capabilities: ['Add or remove instances and choose their private IP'],
    relatedTools: ['vpc_list', 'platform_help'],
    suggestedPrompts: [
      'Will adding a server restart it?',
      'How do I choose a private IP?',
      'Why does my server not get its private IP?',
    ],
  },
  {
    id: 'resize-wizard',
    match: '/resize-wizard',
    link: '/resize-wizard?vmId=:vmId',
    title: 'Resize',
    summary:
      'Change ONE instance’s plan (upgrade or downgrade), disks, IP addresses and add-ons, with a price quote before applying. Open it for that instance: `vmId` is its id (the `id` of `/instances/:id`, or from account_summary).',
    capabilities: ['Compare plans and see the price difference', 'Add disk space, data disks, IPs or add-ons'],
    relatedTools: ['catalog_plans', 'catalog_quote', 'vm_get', 'platform_help'],
    suggestedPrompts: [
      'Which plan should I upgrade to?',
      'Will resizing restart my server?',
      'Can I shrink my disk?',
    ],
  },
  {
    id: 'clone-wizard',
    match: '/clone-wizard',
    title: 'Clone',
    summary:
      'Copy one instance (its current state or one of its backups) onto another existing instance in the same location. Add `?originVmId=<instance id>` to preselect the instance to copy from.',
    capabilities: ['Choose the origin, the destination and the source (current state or a backup)'],
    relatedTools: ['vm_backups', 'account_summary', 'platform_help'],
    suggestedPrompts: [
      'What does cloning overwrite?',
      'Can I clone to another location?',
      'Can I clone from a backup?',
    ],
  },
  {
    id: 'images',
    match: '/images',
    title: 'Custom images',
    summary: 'Your uploaded disk images and ISOs: upload, renew before they expire, deploy from them.',
    capabilities: ['Upload an ISO or disk image', 'Renew or delete an image', 'Deploy an instance from an image'],
    relatedTools: ['account_images', 'platform_help'],
    suggestedPrompts: [
      'Which image formats can I upload?',
      'When do my images expire?',
      'Why can I not log in to a server built from my image?',
    ],
  },
  {
    id: 'iso',
    match: '/iso',
    title: 'ISO images',
    summary: 'ISO images you can mount on an instance to install or boot another system.',
    capabilities: ['Mount an ISO on an instance', 'Upload your own ISO'],
    relatedTools: ['account_images', 'platform_help'],
    suggestedPrompts: [
      'How do I boot my server from an ISO?',
      'Can I upload my own ISO?',
      'How long does an ISO stay mounted?',
    ],
  },
  {
    id: 'security',
    match: '/security',
    title: 'Security Center',
    summary: 'External security scans of your instances: score, findings and scan settings.',
    capabilities: ['See the security score and findings', 'Run a scan or change the scan schedule'],
    relatedTools: ['platform_help'],
    suggestedPrompts: [
      'What does the Security Center scan?',
      'How do I fix this finding?',
      'How often are my servers scanned?',
    ],
  },
  {
    id: 'security-findings',
    match: '/security/*',
    title: 'Security findings',
    summary: 'The issues external scans found on your instances, by severity.',
    capabilities: ['Review findings and accept a risk for a while'],
    relatedTools: ['platform_help'],
    suggestedPrompts: [
      'Which findings should I fix first?',
      'How do I close an open port?',
      'What does accepting a risk do?',
    ],
  },
  {
    id: 'settings-activity',
    match: '/settings/activity',
    title: 'Activity log',
    summary:
      'Everything that changed on the account — who did it (you, an API token, the AI assistant, support or the platform), when, and whether it worked.',
    capabilities: ['Filter the log by who acted, category, outcome and date'],
    relatedTools: ['account_activity'],
    suggestedPrompts: [
      'Who restarted my server?',
      'What changed on my account this week?',
      'What has the AI assistant done on my account?',
    ],
  },
  {
    id: 'console',
    match: '/console/*',
    title: 'VM Console (VNC)',
    summary:
      'Live VNC console of an instance — the user sees the VM screen exactly as' +
      ' a physical monitor would show it. A screenshot of the current screen is' +
      ' attached to questions so the assistant can read on-screen output.',
    capabilities: [
      'View and interact with the VM screen (keyboard/mouse)',
      'Send Ctrl+Alt+Del, restart, reconnect, go fullscreen',
      'Ask the assistant to analyze what the screen is showing',
      'In rescue mode, use the rescue system menu, and exit rescue mode from Tools',
    ],
    relatedTools: ['vm_status', 'vm_power', 'vm_rescue_status', 'platform_help'],
    suggestedPrompts: [
      'What is my screen showing right now?',
      'Explain the error on my console screen',
      "My server won't boot — help me troubleshoot",
    ],
  },
  {
    id: 'settings-api-tokens',
    match: '/settings/api-tokens',
    title: 'API Tokens',
    summary:
      'Manage personal access tokens that authenticate API/MCP requests — including the one the assistant uses.',
    capabilities: [
      'Create, roll and revoke API tokens',
      'See which scopes each token grants',
    ],
    relatedTools: ['platform_help'],
    suggestedPrompts: [
      'What is an API token used for?',
      'Which scopes should I grant for read-only access?',
      'How do I revoke a token?',
    ],
  },
]

const FALLBACK: ScreenDoc = {
  id: 'general',
  match: '*',
  title: 'Console',
  summary: 'The cloud console.',
  capabilities: ['Manage instances, networking and billing'],
  relatedTools: ['account_summary', 'platform_help'],
  suggestedPrompts: [
    'What can you help me with?',
    'Give me a summary of my account',
    'List my instances',
  ],
}

/** Strip locale prefix + trailing slash so `/en-GB/instances/5` matches `/instances/*`. */
function normalizePath(path: string): string {
  const withoutLocale = path.replace(/^\/[a-z]{2}(-[A-Z]{2})?(?=\/)/, '')
  const trimmed = withoutLocale.replace(/\/+$/, '')
  return trimmed || '/'
}

/**
 * `/x/*` matches the pages UNDER `/x` only, never `/x` itself. It used to match
 * the bare path too, and since the longest pattern wins, `/instances/*` beat
 * the exact `/instances`: the instance LIST resolved to the instance-detail
 * doc and offered "Reboot this instance" on a page with no instance on it.
 */
function matches(pattern: string, path: string): boolean {
  if (pattern === '*') return true
  if (pattern.endsWith('/*')) {
    const base = pattern.slice(0, -2)
    return path.startsWith(`${base}/`)
  }
  return pattern === path
}

/** Resolve the most specific screen doc for a route path. */
export function resolveScreen(path: string): ScreenDoc {
  const p = normalizePath(path)
  // Prefer the longest (most specific) matching pattern.
  const candidates = SCREENS.filter((s) => matches(s.match, p)).sort(
    (a, b) => b.match.length - a.match.length,
  )
  return candidates[0] ?? FALLBACK
}

export function suggestedPromptsForRoute(path: string): string[] {
  return resolveScreen(path).suggestedPrompts
}

/** One screen in the navigation site map sent to the assistant. */
export interface SiteMapScreen {
  route: string
  title: string
  parent: string | null
  summary?: string
}

/**
 * The COMPLETE navigable site map: every route harvested from the pages
 * (full feature coverage), enriched with the curated summary for the key
 * screens. This is what lets the assistant give step-by-step directions for any
 * feature, not just the hand-curated few.
 */
export function fullSiteMap(): SiteMapScreen[] {
  return GENERATED_SCREENS.map((g) => {
    const curated = resolveScreen(g.route)
    const hasCurated = curated.id !== 'general'
    return {
      route: hasCurated && curated.link ? curated.link : g.route,
      title: hasCurated ? curated.title : g.title,
      parent: g.parent,
      summary: hasCurated ? curated.summary : undefined,
    }
  })
}
